import { useState } from 'react';
import { styled } from '@mui/material/styles';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Badge,
  Button,
  Container,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  ListItemText,
  Drawer,
  List,
  ListItemButton,
} from '@mui/material';
import {
  Inventory as InventoryIcon,
  Notifications as NotificationsIcon,
  Settings as SettingsIcon,
  Storage as DatabaseIcon,
  CloudSync as CloudSyncIcon,
  History as LogsIcon,
  MonitorHeart as HealthIcon,
  Category as AttributesIcon,
  Logout as LogoutIcon,
  Menu as MenuIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const AppBarStyled = styled(AppBar)({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  boxShadow: '0 4px 20px 0 rgba(0,0,0,0.15)',
  backdropFilter: 'blur(10px)',
});

const NavButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active?: boolean }>(({ active }) => ({
  color: 'white',
  padding: '8px 20px',
  borderRadius: '12px',
  textTransform: 'none',
  fontSize: '0.95rem',
  fontWeight: active ? 600 : 400,
  position: 'relative',
  transition: 'all 0.3s ease',
  backgroundColor: active ? 'rgba(255,255,255,0.2)' : 'transparent',
  '&:hover': {
    backgroundColor: 'rgba(255,255,255,0.15)',
    transform: 'translateY(-2px)',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: '50%',
    transform: active ? 'translateX(-50%) scaleX(1)' : 'translateX(-50%) scaleX(0)',
    width: '60%',
    height: '3px',
    backgroundColor: 'white',
    borderRadius: '3px 3px 0 0',
    transition: 'transform 0.3s ease',
  },
  '&:hover::after': {
    transform: 'translateX(-50%) scaleX(1)',
  },
  '& .MuiButton-startIcon': {
    marginRight: '8px',
  },
}));

const menuItems = [
  { text: 'DB Proizvodi', icon: <DatabaseIcon />, path: '/db-products' },
  { text: 'Feed Manager', icon: <InventoryIcon />, path: '/feed-manager' },
  { text: 'WooSync', icon: <CloudSyncIcon />, path: '/woo-sync' },
  { text: 'Atributi', icon: <AttributesIcon />, path: '/attribute-values' },
  { text: 'Logovi', icon: <LogsIcon />, path: '/logs' },
  { text: 'Health', icon: <HealthIcon />, path: '/sync-health' },
];

function getInitials(email?: string | null): string {
  if (!email) return '?';
  // Iz info@sejkan.com → "IS" (prvo slovo localpart-a + prvo domena)
  const [local, domain = ''] = email.split('@');
  const a = (local[0] || '').toUpperCase();
  const b = (domain[0] || '').toUpperCase();
  return (a + b) || '?';
}

export default function Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(menuAnchor);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleDrawerNavigate = (path: string) => {
    setDrawerOpen(false);
    navigate(path);
  };

  const handleLogout = async () => {
    setMenuAnchor(null);
    setDrawerOpen(false);
    try {
      await logout();
      navigate('/login', { replace: true });
    } catch (e) {
      console.error('Logout failed:', e);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBarStyled position="fixed">
        <Container maxWidth="xl">
          <Toolbar sx={{ justifyContent: 'space-between', px: { xs: 0, sm: 2 } }}>
            {/* Hamburger (mobile) */}
            <IconButton
              color="inherit"
              edge="start"
              aria-label="Otvori meni"
              onClick={() => setDrawerOpen(true)}
              sx={{ display: { xs: 'inline-flex', md: 'none' }, mr: 1 }}
            >
              <MenuIcon />
            </IconButton>

            {/* Logo */}
            <Typography
              variant="h5"
              noWrap
              component="div"
              sx={{
                fontWeight: 700,
                letterSpacing: '-0.5px',
                background: 'linear-gradient(45deg, #fff 30%, #e0e7ff 90%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                cursor: 'pointer',
              }}
              onClick={() => navigate('/')}
            >
              SejkanSync
            </Typography>

            {/* Navigation Menu */}
            <Box sx={{
              display: { xs: 'none', md: 'flex' },
              gap: 1,
              alignItems: 'center',
            }}>
              {menuItems.map((item) => (
                <NavButton
                  key={item.text}
                  active={location.pathname === item.path}
                  startIcon={item.icon}
                  onClick={() => navigate(item.path)}
                >
                  {item.text}
                </NavButton>
              ))}
            </Box>

            {/* Right Side Icons */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                color="inherit"
                onClick={() => {
                  // Placeholder za notifikacije - možeš dodati modal ili dropdown
                  console.log('Notifikacije kliknute');
                }}
                sx={{
                  display: { xs: 'none', sm: 'inline-flex' },
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    transform: 'scale(1.1)',
                    transition: 'all 0.2s ease'
                  }
                }}
              >
                <Badge badgeContent={3} color="error">
                  <NotificationsIcon />
                </Badge>
              </IconButton>
              <IconButton
                color="inherit"
                onClick={() => navigate('/settings')}
                sx={{
                  display: { xs: 'none', sm: 'inline-flex' },
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    transform: 'rotate(90deg)',
                    transition: 'all 0.3s ease'
                  }
                }}
              >
                <SettingsIcon />
              </IconButton>
              <Avatar
                onClick={(e) => setMenuAnchor(e.currentTarget)}
                aria-controls={menuOpen ? 'user-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={menuOpen ? 'true' : undefined}
                sx={{
                  width: 40,
                  height: 40,
                  border: '2px solid rgba(255,255,255,0.3)',
                  cursor: 'pointer',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  '&:hover': {
                    transform: 'scale(1.1)',
                    transition: 'transform 0.2s ease',
                    boxShadow: '0 0 20px rgba(255,255,255,0.3)',
                  }
                }}
              >
                {getInitials(user?.email)}
              </Avatar>
              <Menu
                id="user-menu"
                anchorEl={menuAnchor}
                open={menuOpen}
                onClose={() => setMenuAnchor(null)}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                PaperProps={{
                  sx: {
                    mt: 1,
                    minWidth: 240,
                    borderRadius: 2,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                  }
                }}
              >
                <Box sx={{ px: 2, py: 1.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
                    Prijavljen kao
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500, wordBreak: 'break-all' }}>
                    {user?.email || '—'}
                  </Typography>
                </Box>
                <Divider />
                <MenuItem onClick={handleLogout}>
                  <ListItemIcon>
                    <LogoutIcon fontSize="small" color="error" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Odjava"
                    primaryTypographyProps={{ color: 'error', fontWeight: 500 }}
                  />
                </MenuItem>
              </Menu>
            </Box>
          </Toolbar>
        </Container>
      </AppBarStyled>

      {/* Mobile Drawer */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{ display: { xs: 'block', md: 'none' } }}
        PaperProps={{ sx: { width: 280 } }}
      >
        <Box sx={{ px: 2, py: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            SejkanSync
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
            {user?.email || ''}
          </Typography>
        </Box>
        <Divider />
        <List>
          {menuItems.map((item) => (
            <ListItemButton
              key={item.text}
              selected={location.pathname === item.path}
              onClick={() => handleDrawerNavigate(item.path)}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          ))}
        </List>
        <Divider />
        <List>
          <ListItemButton onClick={handleLogout}>
            <ListItemIcon>
              <LogoutIcon color="error" />
            </ListItemIcon>
            <ListItemText
              primary="Odjava"
              primaryTypographyProps={{ color: 'error', fontWeight: 500 }}
            />
          </ListItemButton>
        </List>
      </Drawer>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          pt: '64px', // AppBar height
          minHeight: '100vh',
          backgroundColor: '#f8fafc',
        }}
      >
        <Container maxWidth={false} sx={{ py: { xs: 2, md: 4 }, px: { xs: 1.5, sm: 3, md: 4 } }}>
          <Outlet />
        </Container>
      </Box>
    </Box>
  );
}
